/*
 * PlantShellPage — Shell-Tab in der Plant-Detailseite.
 *
 * Interaktive Remote-Shell gegen einen Field-Client der Anlage. Wire liegt
 * in docs/wire-remote-shell.md, Server-Slice unter src/features/RemoteShell/.
 *
 * Aktueller Scope (Slice 1):
 *   - Session-Lifecycle: start, keepalive (auto alle 4 min), close on unmount.
 *   - Command-Send + Report-Polling. Report enthaelt exitCode + SHA-256 der
 *     Streams, NICHT die Roh-Ausgabe (Slice-2-Roadmap: SignalR-Kanal).
 *   - Bash-Syntax-Highlight fuer eingegebene Commands (bashHighlight.ts —
 *     rein regex-basiert, keine externe Abhaengigkeit).
 *   - Command-History-Navigation mit ArrowUp / ArrowDown.
 *
 * Empty-States:
 *   - 403 vom Session-Start: Kein remote-shell-Recht — Hinweis zeigen.
 *   - Anlage hat keine Field-Clients: Hinweis + Zurueck-Link.
 *
 * Nicht implementiert (noch nicht):
 *   - Roh-Live-Output (kommt mit SignalR-Kanal, Slice 2).
 *   - Kommando-Whitelist / Sandbox-Profile.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import type {
  ListPlantsOverviewResponse,
  PlantOverviewClientDto,
  PlantOverviewDto,
  ShellCommandReportResponse,
  StartShellSessionResponse,
  SendShellCommandResponse,
  KeepaliveShellSessionResponse,
} from '../api/types';
import { highlightBash } from './bashHighlight';
import './PlantShellPage.css';

interface Props { id?: string }

interface HistoryEntry {
  commandId: string | null;
  command: string;
  issuedAt: number;
  status: 'sending' | 'issued' | 'completed' | 'timeout' | 'rejected' | 'error';
  exitCode: number | null;
  durationMs: number | null;
  stdoutSha256: string | null;
  stderrSha256: string | null;
  stdout: string | null;
  stderr: string | null;
  truncated: boolean;
  error: string | null;
}

export function PlantShellPage({ id }: Props) {
  if (!id) {
    return (
      <AppShell title="Shell">
        <p class="hv-plant-shell__empty">Keine Anlagen-ID in der URL.</p>
      </AppShell>
    );
  }

  const plantsState = useAsync(
    (signal) => api.get<ListPlantsOverviewResponse>('/api/plants/overview?pageSize=500', signal),
    [id],
  );

  const plant: PlantOverviewDto | null = useMemo(() => {
    if (plantsState.status !== 'ok') return null;
    return plantsState.data.plants.find((p) => p.id === id) ?? null;
  }, [plantsState, id]);

  const clients = plant?.clients ?? [];
  // ?client=… waehlt vor, etwa aus "Anlagen, Touchpanels".
  const wanted = (useLocation().query as Record<string, string>).client || null;
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedClientId !== null) return;
    if (wanted && clients.some((c) => c.id === wanted)) { setSelectedClientId(wanted); return; }
    if (clients.length > 0) setSelectedClientId(plant?.primaryClient?.id ?? clients[0].id);
  }, [clients, selectedClientId, plant]);

  return (
    <AppShell title="Shell">
      <PlantSubNav plantId={id} plantName={plant?.displayName} />
      <div class="hv-plant-shell">
        {plantsState.status === 'loading' && (
          <p class="hv-plant-shell__empty">Lade Anlagendaten…</p>
        )}
        {plantsState.status === 'error' && (
          <p class="hv-plant-shell__empty">Anlage konnte nicht geladen werden.</p>
        )}
        {plantsState.status === 'ok' && plant && (
          clients.length === 0
            ? <NoClientsHint />
            : (
              <ShellTerminal
                plantId={id}
                clients={clients}
                selectedClientId={selectedClientId}
                onSelectClient={setSelectedClientId}
              />
            )
        )}
      </div>
    </AppShell>
  );
}

function NoClientsHint() {
  return (
    <div class="hv-plant-shell__empty">
      <p>Diese Anlage hat noch keinen registrierten Field-Client. Erst wenn ein Panel oder Gateway per mTLS-Enrollment angeschlossen ist, kann eine Shell-Session gefahren werden.</p>
    </div>
  );
}

interface TerminalProps {
  plantId: string;
  clients: PlantOverviewClientDto[];
  selectedClientId: string | null;
  onSelectClient: (id: string) => void;
}

function ShellTerminal({ plantId, clients, selectedClientId, onSelectClient }: TerminalProps) {
  const [session, setSession] = useState<StartShellSessionResponse | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [starting, setStarting] = useState<boolean>(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [input, setInput] = useState<string>('');
  const [cursor, setCursor] = useState<number>(-1); // history navigation
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sessionRef = useRef<StartShellSessionResponse | null>(null);
  sessionRef.current = session;

  // Auto-scroll to bottom when history grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history]);

  // Keep-alive every 4 min while a session is open (server TTL default 10 min,
  // safe margin). Cleared on close / unmount.
  useEffect(() => {
    if (!session) return;
    const handle = window.setInterval(async () => {
      const current = sessionRef.current;
      if (!current) return;
      try {
        const r = await api.post<KeepaliveShellSessionResponse>(
          `/api/shell/sessions/${current.sessionId}/keepalive`,
          {},
        );
        setSession((prev) => prev ? { ...prev, expiresAt: r.expiresAt } : prev);
      } catch {
        // Keepalive failing means the session is gone; drop it.
        setSession(null);
      }
    }, 4 * 60 * 1000);
    return () => window.clearInterval(handle);
  }, [session?.sessionId]);

  // Close session on unmount so a browser tab close does not leak a live handle
  // on the field-client. The Session-Cleaner would clean it up after TTL, but
  // this is more polite.
  useEffect(() => {
    return () => {
      const current = sessionRef.current;
      if (!current) return;
      api.delete(`/api/shell/sessions/${current.sessionId}`).catch(() => { /* best-effort */ });
    };
  }, []);

  const startSession = async () => {
    if (!selectedClientId) return;
    setStarting(true);
    setSessionError(null);
    try {
      const r = await api.post<StartShellSessionResponse>(
        `/api/plants/${plantId}/clients/${selectedClientId}/shell/sessions`,
        {},
      );
      setSession(r);
      setHistory([]);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      const err = e as ApiError;
      if (err?.status === 403) {
        setSessionError('Ihr Konto hat die Berechtigung remote.shell nicht. Ohne die kann keine Shell-Session gestartet werden — Vergabe ueber /settings/identity.');
      } else if (err?.status === 503) {
        setSessionError('Remote-Shell serverseitig deaktiviert.');
      } else {
        setSessionError(err?.message ?? 'Session konnte nicht gestartet werden.');
      }
    } finally {
      setStarting(false);
    }
  };

  const closeSession = async () => {
    if (!session) return;
    try { await api.delete(`/api/shell/sessions/${session.sessionId}`); }
    catch { /* best-effort */ }
    setSession(null);
  };

  const submitCommand = async () => {
    if (!session) return;
    const trimmed = input.trim();
    if (trimmed.length === 0) return;
    const entry: HistoryEntry = {
      commandId: null,
      command: trimmed,
      issuedAt: Date.now(),
      status: 'sending',
      exitCode: null,
      durationMs: null,
      stdoutSha256: null,
      stderrSha256: null,
      stdout: null,
      stderr: null,
      truncated: false,
      error: null,
    };
    setHistory((h) => [...h, entry]);
    setInput('');
    setCursor(-1);
    const index = history.length;
    try {
      const r = await api.post<SendShellCommandResponse>(
        `/api/shell/sessions/${session.sessionId}/commands`,
        { command: trimmed },
      );
      updateEntry(index, { commandId: r.commandId, status: 'issued' });
      pollReport(session.sessionId, r.commandId, r.timeoutSeconds, index);
    } catch (e) {
      const err = e as ApiError;
      updateEntry(index, {
        status: 'error',
        error: err?.status === 429
          ? 'Rate-Limit: max. 60 Commands/Minute pro Session.'
          : err?.message ?? 'Command fehlgeschlagen.',
      });
    }
  };

  const updateEntry = (idx: number, patch: Partial<HistoryEntry>) => {
    setHistory((h) => h.map((e, i) => i === idx ? { ...e, ...patch } : e));
  };

  const pollReport = (sessionId: string, commandId: string, timeoutSec: number, idx: number) => {
    const start = Date.now();
    const deadline = start + (timeoutSec + 10) * 1000;
    const tick = async () => {
      try {
        const r = await api.get<ShellCommandReportResponse>(
          `/api/shell/sessions/${sessionId}/commands/${commandId}/report`,
        );
        if (r.status !== 'issued') {
          updateEntry(idx, {
            status: r.status,
            exitCode: r.exitCode,
            durationMs: r.durationMs,
            stdoutSha256: r.stdoutSha256,
            stderrSha256: r.stderrSha256,
            stdout: r.stdout,
            stderr: r.stderr,
            truncated: r.truncated,
          });
          return;
        }
      } catch {
        // Poll error — retry on next tick until deadline.
      }
      if (Date.now() > deadline) {
        updateEntry(idx, { status: 'timeout', error: 'Field-Client hat nicht geantwortet.' });
        return;
      }
      window.setTimeout(tick, 800);
    };
    window.setTimeout(tick, 400);
  };

  const historyCommands = useMemo(
    () => history.filter((h) => h.command.length > 0).map((h) => h.command),
    [history],
  );

  const onInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitCommand();
      return;
    }
    if (e.key === 'ArrowUp') {
      if (historyCommands.length === 0) return;
      e.preventDefault();
      const nextCursor = cursor === -1 ? historyCommands.length - 1 : Math.max(0, cursor - 1);
      setCursor(nextCursor);
      setInput(historyCommands[nextCursor]);
      return;
    }
    if (e.key === 'ArrowDown') {
      if (cursor === -1) return;
      e.preventDefault();
      const nextCursor = cursor + 1;
      if (nextCursor >= historyCommands.length) {
        setCursor(-1);
        setInput('');
      } else {
        setCursor(nextCursor);
        setInput(historyCommands[nextCursor]);
      }
    }
  };

  const client = clients.find((c) => c.id === selectedClientId) ?? null;

  return (
    <div class="hv-plant-shell__terminal">
      <div class="hv-plant-shell__toolbar">
        <label class="hv-plant-shell__toolbar-field">
          Field-Client:
          <select
            value={selectedClientId ?? ''}
            onChange={(e) => onSelectClient((e.currentTarget as HTMLSelectElement).value)}
            disabled={session !== null}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.chassisDisplayName ?? c.hardwareKind ?? c.mqttClientName}
                {c.osKind ? ` · ${c.osKind}` : ''}
              </option>
            ))}
          </select>
        </label>
        {session === null ? (
          <button
            type="button"
            class="hv-plant-shell__btn hv-plant-shell__btn--primary"
            onClick={startSession}
            disabled={starting || selectedClientId === null}
          >
            {starting ? 'Starte…' : 'Session starten'}
          </button>
        ) : (
          <>
            <span class="hv-plant-shell__badge">Session laeuft — Ablauf {new Date(session.expiresAt).toLocaleTimeString()}</span>
            <button type="button" class="hv-plant-shell__btn" onClick={closeSession}>
              Session schliessen
            </button>
          </>
        )}
      </div>

      {sessionError && (
        <div class="hv-plant-shell__banner hv-plant-shell__banner--warn">
          {sessionError}
        </div>
      )}

      {session && client && (
        <>
          <div class="hv-plant-shell__screen" ref={scrollRef}>
            {history.length === 0 && (
              <div class="hv-plant-shell__hint">
                Session offen gegen {client.chassisDisplayName ?? client.mqttClientName}. Enter zum Absetzen, Pfeil-Hoch/-Runter fuer Command-History.<br />
                Hinweis Slice 1: der Field-Client meldet zurueck nur Exit-Code + SHA-256 der stdout/stderr-Streams. Roh-Ausgabe im Browser-Terminal folgt in Slice 2 (SignalR-Kanal).
              </div>
            )}
            {history.map((h, i) => (
              <ShellHistoryLine key={i} entry={h} />
            ))}
          </div>

          <div class="hv-plant-shell__input-row">
            <span class="hv-plant-shell__prompt">$</span>
            <input
              ref={inputRef}
              class="hv-plant-shell__input"
              type="text"
              value={input}
              onInput={(e) => setInput((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={onInputKeyDown}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellcheck={false}
              placeholder="sh command…"
            />
          </div>
        </>
      )}
    </div>
  );
}

function ShellHistoryLine({ entry }: { entry: HistoryEntry }) {
  const statusTone =
    entry.status === 'completed' && entry.exitCode === 0 ? 'ok'
    : entry.status === 'completed' ? 'warn'
    : entry.status === 'timeout' || entry.status === 'rejected' || entry.status === 'error' ? 'error'
    : 'info';
  const hasStdout = !!entry.stdout && entry.stdout.length > 0;
  const hasStderr = !!entry.stderr && entry.stderr.length > 0;

  // Meta-line only after a terminal state — while sending / waiting, we
  // show an inline hint on the command line instead of a stale ok-line.
  const showMeta = entry.status === 'completed'
    || entry.status === 'timeout'
    || entry.status === 'rejected'
    || entry.status === 'error';

  return (
    <div class="hv-plant-shell__row">
      <div class="hv-plant-shell__row-cmd">
        <span class="hv-plant-shell__prompt">$</span>
        <code
          class="hv-plant-shell__row-code"
          dangerouslySetInnerHTML={{ __html: highlightBash(entry.command) }}
        />
        {(entry.status === 'sending' || entry.status === 'issued') && (
          <span class="hv-plant-shell__row-inflight">
            {entry.status === 'sending' ? '· senden…' : '· warte auf Field-Client…'}
          </span>
        )}
      </div>
      {hasStdout && <pre class="hv-plant-shell__row-out">{entry.stdout}</pre>}
      {hasStderr && <pre class="hv-plant-shell__row-out hv-plant-shell__row-out--err">{entry.stderr}</pre>}
      {showMeta && (
        <div class={`hv-plant-shell__row-meta hv-plant-shell__row-meta--${statusTone}`}>
          {entry.status === 'completed' && (
            <>
              <span>exit {entry.exitCode ?? '?'}</span>
              {entry.durationMs != null && <span>· {entry.durationMs} ms</span>}
            </>
          )}
          {entry.status === 'timeout' && (
            <>
              <span>timeout</span>
              {entry.durationMs != null && <span>· {entry.durationMs} ms</span>}
            </>
          )}
          {entry.status === 'rejected' && <span>Client abgelehnt</span>}
          {entry.status === 'error' && <span>{entry.error ?? 'Fehler'}</span>}
          {entry.truncated && <span class="hv-plant-shell__row-meta-warn">truncated</span>}
          {entry.stdoutSha256 && (
            <span class="hv-plant-shell__row-meta-hash" title={`stdout SHA-256: ${entry.stdoutSha256}`}>
              stdout {entry.stdoutSha256.slice(0, 8)}…
            </span>
          )}
          {entry.stderrSha256 && (
            <span class="hv-plant-shell__row-meta-hash" title={`stderr SHA-256: ${entry.stderrSha256}`}>
              stderr {entry.stderrSha256.slice(0, 8)}…
            </span>
          )}
        </div>
      )}
    </div>
  );
}
